type SupportedSurveyErrorEmailLanguage = 'en' | 'fr';

interface SurveyErrorSupportEmailBodyParams {
  language?: string;
  missionAssignationId?: string;
  errorStatus?: number | string;
  errorResponse?: unknown;
}

interface SurveyErrorSupportMailtoParams extends SurveyErrorSupportEmailBodyParams {
  to: string;
  subject: string;
}

const emailTextByLanguage: Record<
  SupportedSurveyErrorEmailLanguage,
  {
    intro: string;
    requestedInfo: string[];
    debugInfo: string;
  }
> = {
  en: {
    intro: 'Please complete the following information before sending this email:',
    requestedInfo: [
      'Website or application used?',
      'Debugging information (available by clicking “Show error details”)',
      'Device used and operating system version (e.g., iPhone, Samsung, MacBook, tablet)',
      'Date and time when the error occurred',
      'Screenshot of the issue',
    ],
    debugInfo: 'Debug info:',
  },
  fr: {
    intro: "Veuillez compléter les informations suivantes avant d'envoyer ce courriel :",
    requestedInfo: [
      'Web ou application utilisée ?',
      "Informations de débogage (disponibles en cliquant sur « Afficher les détails de l'erreur »)",
      'Appareil utilisé et version du système (ex. : iPhone, Samsung, MacBook, tablette)',
      "Date et heure de l'erreur",
      "Capture d'écran du problème",
    ],
    debugInfo: 'Informations de débogage :',
  },
};

const getEmailLanguage = (language?: string): SupportedSurveyErrorEmailLanguage => (language?.toLowerCase().startsWith('fr') ? 'fr' : 'en');

const sensitiveErrorKeys = new Set([
  'authorization',
  'headers',
  'config',
  'request',
  'token',
  'accesstoken',
  'idtoken',
  'customtoken',
]);

const redactSensitiveErrorText = (value: string) => value
  .replace(/\b(Firebase|Bearer|MSCustom)\s+[^\s,;"']+/gi, '$1 [Redacted]');

const getSanitizedErrorJsonReplacer = () => {
  const seen = new WeakSet<object>();

  return (key: string, value: unknown) => {
    if (sensitiveErrorKeys.has(key.replace(/[-_]/g, '').toLowerCase())) {
      return undefined;
    }

    if (typeof value === 'string') {
      return redactSensitiveErrorText(value);
    }

    if (value && typeof value === 'object') {
      if (seen.has(value)) {
        return '[Circular]';
      }

      seen.add(value);
    }

    return value;
  };
};

const getSafeErrorResponse = (errorResponse: unknown) => {
  if (!errorResponse || typeof errorResponse !== 'object') {
    return errorResponse;
  }

  const response = errorResponse as { data?: unknown; status?: unknown; statusText?: unknown; headers?: unknown };

  if ('data' in response && ('status' in response || 'statusText' in response || 'headers' in response)) {
    return response.data ?? {
      status: response.status,
      statusText: response.statusText,
    };
  }

  return errorResponse;
};

export const formatSurveyErrorResponse = (errorResponse: unknown): string => {
  const safeErrorResponse = getSafeErrorResponse(errorResponse);

  if (safeErrorResponse === undefined || safeErrorResponse === null || safeErrorResponse === '') {
    return 'Unknown error';
  }

  if (safeErrorResponse instanceof Error) {
    return redactSensitiveErrorText(safeErrorResponse.message);
  }

  if (typeof safeErrorResponse !== 'object') {
    return redactSensitiveErrorText(String(safeErrorResponse));
  }

  try {
    return JSON.stringify(safeErrorResponse, getSanitizedErrorJsonReplacer()) ?? redactSensitiveErrorText(String(safeErrorResponse));
  } catch {
    return redactSensitiveErrorText(String(safeErrorResponse));
  }
};

export const buildSurveyErrorSupportEmailBody = ({
  language,
  missionAssignationId,
  errorStatus,
  errorResponse,
}: SurveyErrorSupportEmailBodyParams): string => {
  const text = emailTextByLanguage[getEmailLanguage(language)];

  return [
    text.intro,
    '',
    ...text.requestedInfo.flatMap((item) => [item, '']),
    text.debugInfo,
    `Mission assignation id: ${missionAssignationId || 'Unknown'}`,
    `Error status: ${errorStatus ?? 'Unknown'}`,
    `Error response: ${formatSurveyErrorResponse(errorResponse)}`,
  ].join('\r\n');
};

export const buildSurveyErrorSupportMailto = ({ to, subject, ...bodyParams }: SurveyErrorSupportMailtoParams): string => {
  const body = buildSurveyErrorSupportEmailBody(bodyParams);

  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};
